import { ImageResponse } from '~/types/files';
import { KsaAuthorizedSignatory } from '~/types/ksa/KsaAuthorizedSignatory';
import { NafathStatus, NafathUpload } from '~/types/Contract';
import axios from './axios';

export const generateContract = () => {
  return axios
    .get<Blob>('/stages/contract/generate', { responseType: 'blob' })
    .then((res) => {
      return {
        name:
          res.headers['content-disposition']?.split('; ')?.[1]?.split('filename=')?.[1] ||
          'contract.pdf',
        blob: res.data,
      };
    })
    .catch(async (error) => {
      const responseObj = await error?.response?.data.text();
      // eslint-disable-next-line no-param-reassign
      error.response!.data = JSON.parse(responseObj);
      throw error;
    });
};

export const uploadSignedContract = (data: ImageResponse[]) => {
  return axios
    .post<NafathUpload>('/stages/ksa/contract/uploadSignedContract', data)
    .then((res) => res.data);
};

export const getNafathStatus = async () => {
  const res = await axios.get<NafathStatus>('/stages/ksa/contract/nafath/status');
  // comment
  return res.data;
};

export const nafathRetry = () =>
  axios
    .post<NafathUpload>('/stages/ksa/contract/nafath/retry')
    .then((res) => res.data);

export const getSignatoriesInfo = () => {
  return axios
    .get<{ signatories: KsaAuthorizedSignatory[] }>('/stages/ksa/contract/signatories/info')
    .then((res) => res.data);
};

export const resetAttempts = (uuid: string) => {
  return axios.post(`/stages/ksa/contract/signatories/${uuid}/resetAttempts`);
};

export const sendInvitation = (email: string) => {
  return axios.post('/stages/contract/sendInvitation', { email }).then((res) => res.data);
};

export const sendInvitationKsa = (params: { email: string; signatoryUuid: string }) => {
  return axios.post('/stages/ksa/contract/send-invitation', params).then((res) => res.data);
};

export const setSignatory = (signatoryUuid: string) => {
  return axios.post('/stages/ksa/contract/signatory', { signatoryUuid }).then((res) => res.data);
};

export const getSignatories = () => {
  return axios
    .get<{ signatories: KsaAuthorizedSignatory[] }>('/stages/ksa/contract/signatories')
    .then((res) => res.data);
};

export const downloadContractUnderReview = () => {
  return axios.get<Blob>('/stages/submit/download', { responseType: 'blob' }).then((res) => {
    return {
      name:
        res.headers['content-disposition']?.split('; ')?.[1]?.split('filename=')?.[1] ||
        'contract.pdf',
      blob: res.data,
    };
  });
};

export const signWithUaePass = () => {
  return axios.post('/stages/contract/signWithUaePass').then((res) => res.data);
};

export const fetchDocumentFromUaePass = () => {
  return axios.post('/stages/contract/fetchDocumentFromUaePass').then((res) => res.data);
};

// ─── T&C and Fees ─────────────────────────────────────────────────────────────

export interface TermsAndConditionsContent {
  version?: string;
  lang?: string;
  /** Raw HTML string to render inside the modal */
  contentHtml: string;
}

// ─── Fees & Charges API response types ───────────────────────────────────────

export interface FeeAmount {
  value: number;
  currency?: string;
}

export interface FeeLine {
  label: string;
  /** Percentage fee (e.g. 1 means 1%) */
  percent?: number;
  amount?: FeeAmount;
  /** Cap on the amount (e.g. max SAR 160) */
  amountCap?: FeeAmount;
  /** If true the fee is waived / free */
  waived?: boolean;
  /** Conditional text shown below the row (e.g. "If monthly sales below SAR 15,000") */
  condition?: string;
}

export interface FeeSection {
  label: string;
  lines: FeeLine[];
}

export interface FeeProduct {
  key: string;
  label: string;
  sections: FeeSection[];
}

export interface FeesAndChargesResponse {
  version?: string;
  currency?: string;
  vat?: { percent: number };
  /** Footer note shown below all fees (e.g. "All prices are VAT excluded") */
  footer?: string;
  products: FeeProduct[];
}

/** TODO: Replace '/stages/ksa/contract/terms-and-conditions' with the real API URL when available */
export const getTermsAndConditions = (lang: string) =>
  axios
    .get<TermsAndConditionsContent>('/stages/ksa/contract/terms-and-conditions', {
      params: { lang },
    })
    .then((res) => res.data);

/** TODO: Replace '/stages/ksa/contract/fees-and-charges' with the real API URL when available */
export const getFeesAndCharges = (lang: string) =>
  axios
    .get<FeesAndChargesResponse>('/stages/ksa/contract/fees-and-charges', {
      params: { lang },
    })
    .then((res) => res.data);

/**
 * Called when the merchant clicks Submit on the Agreement page.
 * Records that the merchant has accepted the T&C and Fees & Charges.
 */
export const acceptAgreement = () =>
  axios
    .post('/stages/ksa/contract/accept-agreement')
    .then((res) => res.data);
